/**
 * tRPC client — used in components for queries and mutations.
 *
 * Usage:
 *   import { trpc } from "app/trpc/client"
 *   const [data] = trpc.dashboards.getDashboard.useSuspenseQuery({ dashboardId })
 *   const mutation = trpc.auth.login.useMutation()
 */
import { httpBatchLink, loggerLink } from "@trpc/client"
import { createTRPCNext } from "@trpc/next"
import { ssrPrepass } from "@trpc/next/ssrPrepass"
import type { AppRouter } from "app/trpc/routers/_generated"
import superjson from "superjson"

function getBaseUrl() {
  if (typeof window !== "undefined") return ""
  // SSR: use APP_ORIGIN if available, fall back to localhost (#7)
  return process.env.APP_ORIGIN || `http://localhost:${process.env.PORT ?? 3000}`
}

function getAntiCSRFToken(): string {
  if (typeof document === "undefined") return ""
  const match = document.cookie.match(/playerdash_sAntiCsrfToken=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : ""
}

export const trpc = createTRPCNext<AppRouter>({
  config({ ctx }) {
    return {
      links: [
        loggerLink({
          enabled: (opts) => {
            if (opts.direction !== "down" || !(opts.result instanceof Error)) return false
            // Don't log expected auth/not-found errors — they spam server logs during SSR
            const code = (opts.result as any)?.statusCode ?? (opts.result as any)?.data?.httpStatus
            if (code === 401 || code === 403 || code === 404) return false
            return true
          },
        }),
        httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
          headers() {
            const headers: Record<string, string> = {}
            // Forward cookies for SSR requests
            if (ctx?.req?.headers) {
              headers.cookie = ctx.req.headers.cookie ?? ""
            }
            // Send anti-CSRF token for mutation requests (#1)
            const csrfToken = getAntiCSRFToken()
            if (csrfToken) {
              headers["anti-csrf"] = csrfToken
            }
            return headers
          },
        }),
      ],
      queryClientConfig: {
        defaultOptions: {
          queries: { staleTime: 20_000 },
        },
      },
    }
  },
  ssr: true,
  ssrPrepass,
  transformer: superjson,
})
